import { jsx as _jsx } from "react/jsx-runtime";
import { reportErrorToBugsnag } from "@air/utils-error";
import isString from "lodash/isString";
import Router from "next/router";
import { createContext, memo, useContext, useEffect, useState } from "react";
import { ASSET_MODAL_COLLAPSIBLE_SECTIONS } from "~/constants/localStorageKeys";
import { QueryParamNames } from "~/constants/search";
import { isDevOrTestStage } from "~/swr-hooks/utils";
const defaultExpandedState = {
    boards: false,
    descriptionAndTags: true,
    exif: true,
    customFields: true
};
const CollapsibleSectionsContext = /*#__PURE__*/ createContext({
    expandedSections: defaultExpandedState,
    toggleSection: ()=>{}
});
export const AllExpandableSectionId = [
    "boards",
    "descriptionAndTags",
    "exif",
    "customFields"
];
const isExpandableSectionId = (id)=>{
    return AllExpandableSectionId.some((sectionId)=>sectionId === id);
};
export const CollapsibleSectionsContextProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const [expandedSections, setExpandedSections] = useState(defaultExpandedState);
    // Load state on mount
    useEffect(()=>{
        // Check URL query
        const searchParams = new URLSearchParams(window.location.search);
        const expandedSectionsParam = searchParams.get(QueryParamNames.expandedSections);
        const fromUrl = defaultExpandedState;
        // e.g., ?expandedSections=section1,section3
        if (isString(expandedSectionsParam)) {
            const expandedList = expandedSectionsParam.split(",");
            Object.keys(defaultExpandedState).forEach((section)=>{
                if (isExpandableSectionId(section)) {
                    fromUrl[section] = expandedList.includes(section);
                }
            });
            setExpandedSections(fromUrl);
            localStorage.setItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS, JSON.stringify(fromUrl));
            return;
        }
        // If no query, check localStorage
        const saved = localStorage.getItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS);
        if (saved) {
            try {
                setExpandedSections(JSON.parse(saved));
            } catch (error) {
                if (isDevOrTestStage()) {
                    throw error;
                } else {
                    reportErrorToBugsnag({
                        error,
                        context: "Error parsing asset modal collapsible sections"
                    });
                }
            }
            return;
        }
        // Else, use defaults
        setExpandedSections(defaultExpandedState);
    }, []);
    // Toggle a section
    const toggleSection = (sectionId)=>{
        setExpandedSections((prev)=>{
            const newState = {
                ...prev,
                [sectionId]: !prev[sectionId]
            };
            // Update URL query
            const expandedList = Object.keys(newState).filter((id)=>isExpandableSectionId(id) && newState[id]);
            const query = {
                ...Router.query
            };
            if (expandedList.length > 0) {
                query.expanded = expandedList.join(",");
            } else {
                delete query.expanded;
            }
            Router.push({
                query
            }, undefined, {
                shallow: true
            });
            // Save to localStorage
            localStorage.setItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS, JSON.stringify(newState));
            return newState;
        });
    };
    return /*#__PURE__*/ _jsx(CollapsibleSectionsContext.Provider, {
        value: {
            expandedSections,
            toggleSection
        },
        children: children
    });
});
CollapsibleSectionsContextProvider.displayName = "CollapsibleSectionsContextProvider";
export const useCollapsibleSectionsContext = ()=>{
    const context = useContext(CollapsibleSectionsContext);
    if (!context) {
        const error = new Error("useCollapsibleSectionsContext must be used within CollapsibleSectionsContextProvider");
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: "Collapsible sections context not found"
            });
        }
    }
    return context;
};
